import { useEffect, useState, useCallback } from 'react';
import { useSettingsStore } from '../stores/useSettingsStore';

export interface WeatherData {
  condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'thunder' | 'unknown';
  temperature?: number;
  description?: string;
  cityName?: string;
}

const WEATHER_API_KEY = import.meta.env.VITE_WEATHER_API_KEY || '';
const WEATHER_API_URL = 'https://api.weatherapi.com/v1/current.json';

const mapWeatherCondition = (conditionText: string): WeatherData['condition'] => {
  const text = conditionText.toLowerCase();
  
  // WeatherAPI.comの日本語条件文からマッピング
  if (text.includes('雷') || text.includes('thunder')) return 'thunder';
  if (text.includes('雨') || text.includes('rain') || text.includes('小雨')) return 'rainy';
  if (text.includes('雪') || text.includes('snow')) return 'snowy';
  if (text.includes('曇') || text.includes('cloud') || text.includes('overcast')) return 'cloudy';
  if (text.includes('晴') || text.includes('sunny') || text.includes('clear')) return 'sunny';
  
  return 'unknown';
};

export const useWeather = () => {
  const { weatherEnabled } = useSettingsStore();
  const [weather, setWeather] = useState<WeatherData>({ condition: 'unknown' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWeather = useCallback(async (latitude?: number, longitude?: number) => {
    // 設定がオフの場合は取得しない
    if (!weatherEnabled) {
      setWeather({ condition: 'unknown' });
      return;
    }
    
    if (!WEATHER_API_KEY) {
      setWeather({ condition: 'unknown' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let lat = latitude;
      let lon = longitude;

      if (!lat || !lon) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000,
            enableHighAccuracy: false
          });
        });
        lat = position.coords.latitude;
        lon = position.coords.longitude;
      }

      const response = await fetch(
        `${WEATHER_API_URL}?key=${WEATHER_API_KEY}&q=${lat},${lon}&lang=ja`
      );

      if (!response.ok) throw new Error('Weather API request failed');

      const data = await response.json();
      
      // WeatherAPI.comのレスポンス形式に対応
      if (data.error) {
        throw new Error(data.error.message || 'Weather API error');
      }
      
      setWeather({
        condition: mapWeatherCondition(data.current.condition.text),
        temperature: Math.round(data.current.temp_c),
        description: data.current.condition.text,
        cityName: data.location.name
      });
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError('天気情報を取得できませんでした');
      setWeather({ condition: 'sunny' }); // エラー時のデフォルト
    } finally {
      setLoading(false);
    }
  }, [weatherEnabled]);

  useEffect(() => {
    if (weatherEnabled) {
      fetchWeather();
      const interval = setInterval(() => fetchWeather(), 30 * 60 * 1000); // 30分ごと更新
      return () => clearInterval(interval);
    } else {
      setWeather({ condition: 'unknown' });
    }
  }, [fetchWeather, weatherEnabled]);

  return { weather, loading, error, refetch: fetchWeather };
};